// MessagePanelTop.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';
import { format, fromUnixTime } from 'date-fns';

import { SanitizedLink as Link } from '../component/SanitizedLink.jsx';
import getUserProfileUrl from '../resource/getUserProfileUrl.js';
import {
  getDotSize,
  calculateBorderSize,
} from '../component/UserAvatarWithStatus.jsx';
import WithMessagePinData from '../container/WithMessagePinData.js';
import MessageCategory from '../container/MessageCategory.js';
import LinkSubstring from '../container/LinkSubstring.js';
import UserAvatar from '../container/UserAvatar.js';
import WithRealTimeStatus from '../container/WithRealTimeStatus.js';
import UserStatusIndicator from '../container/UserStatusIndicator.js';
import FollowButton from '../container/FollowButton.js';
import {
  LIVESTREAM_TYPE_LIVE_BADGE,
  LIVESTREAM_TYPE_DOT,
} from '../resource/userStatusIndicatorConstants.js';
import { MessageMoreFeature } from '../resource/messageConstants.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import { LIVESTREAM_PATH } from '../resource/liveStreamConstants.js';

import ResetHeadingStyle from '../style/ResetHeadingStyle.js';
import TextEllipsis from '../style/TextEllipsis.js';
import { color } from '../style/variables.js';

import CloseIcon from '../../img/icon-close-outline-shadow.svg';
import DropdownIcon from '../../img/icon-dropDown-outline-shadow.svg';
import MoreIcon from '../../img/icon-more-fill-shadow.svg';

import PinnedGreyIconSource from '../../img/ic_pin_grey.svg';

import { ProfileListType } from '../resource/profileConstants.js';
import getRelativeTime from '../resource/getRelativeTime.js';
import { ButtonId } from '../resource/trackingConstants.js';
import getShareLink from '../resource/getShareLink.js';
import withFetchTranslation from '../resource/withFetchTranslation.js';
import { breakpoint } from '../style/variables.js';
import media from '../style/media.js';

export class MessagePanelTop extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      isTruncated: props.isExpanded ? false : true,
      shouldShowTranslation: false,
      isClient: false,
    };
  }

  static getDerivedStateFromProps(props, state) {
    const { isExpanded } = props;
    if (isExpanded !== !state.isTruncated) {
      return {
        isTruncated: !isExpanded,
      };
    }

    return null;
  }

  componentDidMount() {
    this.nextTick = setTimeout(() => {
      this.setState({ isClient: true });
    });
  }

  componentWillUnmount() {
    clearTimeout(this.nextTick);
  }

  render() {
    return (
      <StyledMessagePanelTop isTruncated={this.state.isTruncated}>
        {this.renderUserRow()}
        {this.renderInfoRow()}
      </StyledMessagePanelTop>
    );
  }

  renderUserRow() {
    const {
      id,
      userId,
      displayedUsername,
      username,
      postedAtUnix,
      scheduledAtUnix,
      showUser,
      goBack,
      isOnDesktop,
      isPublished,
      t,
    } = this.props;
    const { isClient } = this.state;
    if (!showUser) {
      return null;
    }

    const relativeTime = getRelativeTime({ unixTimestamp: postedAtUnix });
    const size = isOnDesktop ? 48 : 32;
    const dotSize = getDotSize({ size });
    const borderSize = calculateBorderSize({ dotSize });

    return (
      <UserRow>
        <AvatarCol>
          <WithRealTimeStatus userId={userId}>
            {({ isOnline, isLiveStreaming }) => {
              return (
                <AvatarWrapper
                  aria-label={`${username}'s avatar`}
                  to={getUserProfileUrl({
                    userId,
                    username,
                    subPath: isLiveStreaming ? LIVESTREAM_PATH : '',
                  })}
                >
                  <div>
                    <UserAvatar id={userId} size={size} />
                    <UserStatusIndicatorWrapper
                      isOnline={isOnline}
                      isLiveStreaming={isLiveStreaming}
                      size={30}
                    >
                      <UserStatusIndicator
                        userId={userId}
                        dotSize={dotSize}
                        borderSize={borderSize}
                        liveStreamType={LIVESTREAM_TYPE_LIVE_BADGE}
                        livestreamSize={14}
                        shouldShowLiveStream
                        shouldShowOnline
                      />
                    </UserStatusIndicatorWrapper>
                  </div>
                </AvatarWrapper>
              );
            }}
          </WithRealTimeStatus>
        </AvatarCol>
        <UsernameCol>
          <UsernameRow>
            <UsernameLink
              to={getUserProfileUrl({ userId, username })}
              data-element_id={ButtonId.Detail.ContentDetailName}
              data-tracking_payload={{
                'user.id': userId,
                'user.username': username,
              }}
            >
              <Username>{displayedUsername}</Username>
              <UserStatusIndicator
                userId={userId}
                liveStreamType={LIVESTREAM_TYPE_DOT}
                livestreamSize={12}
                shouldShowLiveStream
                shouldShowOnline
              />
            </UsernameLink>
          </UsernameRow>
          {/* render formated time on SSR will causing hydration fail due to timezone */}
          {!isClient ? (
            '-'
          ) : !isPublished ? (
            <ScheduledTimeRow>
              {t('schedule_time', {
                ns: TranslationNamespace.POST,
                date: scheduledAtUnix
                  ? format(fromUnixTime(scheduledAtUnix), 'yyyy-MM-dd HH:mm')
                  : '',
              })}
            </ScheduledTimeRow>
          ) : (
            <PostedTimeRow>{relativeTime}</PostedTimeRow>
          )}
        </UsernameCol>
        <ActionCol>
          <FollowButton userId={userId} isBigSize={!!isOnDesktop} />
          {isPublished && (
            <ContextMenuButton
              data-element_id={ButtonId.All.ButtonMore}
              onClick={() => {
                this.props.toggleModal({
                  modalId: 'MessageMoreModal',
                  payload: {
                    messageId: id,
                    pinProfileListType: ProfileListType.STORY,
                    features: [
                      MessageMoreFeature.BLOCK,
                      MessageMoreFeature.FOLLOW,
                      MessageMoreFeature.PROFILE,
                      MessageMoreFeature.PIN,
                      MessageMoreFeature.EDIT,
                      MessageMoreFeature.HIDE,
                    ],
                    shareLink: getShareLink(),
                  },
                  isOpen: true,
                });
              }}
            >
              <img src={MoreIcon} alt="more" width={24} height={24} />
            </ContextMenuButton>
          )}
          <CloseButton
            data-element_id={ButtonId.All.ButtonClose}
            onClick={goBack}
          >
            <img src={CloseIcon} alt="go back" />
          </CloseButton>
        </ActionCol>
      </UserRow>
    );
  }

  renderInfoRow() {
    const {
      id,
      username,
      storyCaption,
      originalCaptionText,
      hasTranslation,
      showUser,
      goBack,
      t,
      currentLanguage,
      sourceLanguage,
    } = this.props;
    const { shouldShowTranslation } = this.state;
    // `message.i18nCaption` concate title and description. There is no need to show title if user want to see translated content.
    const displayCaption =
      shouldShowTranslation && storyCaption
        ? storyCaption
        : originalCaptionText;
    const isLanguageMatched = currentLanguage === sourceLanguage;
    return (
      <InfoRow>
        {!showUser && (
          <ActionCol style={{ float: 'right' }}>
            <CloseButton
              aria-label="go back to the previous page"
              data-element_id={ButtonId.All.ButtonClose}
              onClick={goBack}
            >
              <img src={CloseIcon} alt="go back" />
            </CloseButton>
          </ActionCol>
        )}
        <WithMessagePinData
          messageId={id}
          profileListType={ProfileListType.STORY}
        >
          {({ isPinned }) =>
            isPinned ? (
              <PinnedCol>
                <PinIcon src={PinnedGreyIconSource} />
                <PinText>
                  {t('pinned_post', {
                    ns: TranslationNamespace.POST,
                  })}
                </PinText>
              </PinnedCol>
            ) : null
          }
        </WithMessagePinData>
        <CaptionCol
          isTruncated={this.state.isTruncated}
          onClick={this.handleExpandButtonClick}
        >
          <CaptionColWrapper>
            {displayCaption ? (
              <Caption>
                <LinkSubstring>{displayCaption}</LinkSubstring>
              </Caption>
            ) : (
              <CaptionPlaceholder>
                {t('message_caption_placeholder', {
                  username,
                  ns: TranslationNamespace.POST,
                })}
              </CaptionPlaceholder>
            )}
          </CaptionColWrapper>
          <ExpandButton isTruncated={this.state.isTruncated}>
            <img
              src={DropdownIcon}
              alt={this.state.isTruncated ? 'expand' : 'collapse'}
            />
          </ExpandButton>
        </CaptionCol>
        {!this.state.isTruncated && (
          <TagWrapper>
            <MessageCategory messageId={id} />
            <BottomTagWrapper>
              {hasTranslation && !isLanguageMatched && (
                <>
                  <TranslationToggle
                    role="button"
                    data-element_id={
                      this.state.shouldShowTranslation
                        ? ButtonId.All.ButtonTextOriginal
                        : ButtonId.All.ButtonTextTranslate
                    }
                    onClick={() => {
                      this.setState(prevState => ({
                        shouldShowTranslation: !prevState.shouldShowTranslation,
                      }));
                    }}
                  >
                    {this.state.shouldShowTranslation
                      ? t('show_original', {
                          ns: TranslationNamespace.POST,
                        })
                      : t('show_translate', {
                          ns: TranslationNamespace.POST,
                        })}
                  </TranslationToggle>
                  <MetaPartition>・</MetaPartition>
                </>
              )}
              <WorkFiction>
                {t('broadcast_disclaimer_workoffiction', {
                  ns: TranslationNamespace.POST,
                })}
              </WorkFiction>

              <CopyRight>
                {t('hint_copyright', {
                  ns: TranslationNamespace.POST,
                })}
              </CopyRight>
            </BottomTagWrapper>
          </TagWrapper>
        )}
      </InfoRow>
    );
  }

  handleExpandButtonClick = () => {
    const nextValue = !!this.state.isTruncated;
    this.props.onExpand(nextValue);
    this.setState({ isTruncated: !nextValue });
  };
}

MessagePanelTop.propTypes = {
  id: PropTypes.string,
  userId: PropTypes.string,
  username: PropTypes.string,
  displayedUsername: PropTypes.string,
  storyCaption: PropTypes.string,
  originalCaptionText: PropTypes.string,
  postedAtUnix: PropTypes.number,
  scheduledAtUnix: PropTypes.number,
  showUser: PropTypes.bool,
  hasTranslation: PropTypes.bool,
  isExpanded: PropTypes.bool,
  isOnDesktop: PropTypes.bool,
  isPublished: PropTypes.bool,
  onExpand: PropTypes.func,
  goBack: PropTypes.func,
  toggleModal: PropTypes.func,
  t: PropTypes.func.isRequired,
  currentLanguage: PropTypes.string,
  sourceLanguage: PropTypes.string,
};

MessagePanelTop.defaultProps = {
  id: '',
  userId: null,
  username: null,
  displayedUsername: null,
  storyCaption: null,
  originalCaptionText: '',
  postedAtUnix: 0,
  scheduledAtUnix: 0,
  hasTranslation: false,
  isExpanded: false,
  isOnDesktop: false,
  isPublished: false,
  toggleModal: () => null,
  onExpand: () => null,
  goBack: () => null,
  currentLanguage: null,
  sourceLanguage: null,
};

const StyledMessagePanelTop = styled.header`
  display: flex;
  flex-direction: column;
  pointer-events: none;
  height: ${({ isTruncated }) => (isTruncated ? 'auto' : '100%')};
`;

const UserRow = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 12px;
  pointer-events: auto;
  ${media.mobile`
    padding-bottom: 8px;
  `}
`;

const AvatarCol = styled.div`
  flex: none;
  position: relative;
  margin-right: 16px;
  width: 48px;
  height: 48px;
  ${media.mobile`
    width: 32px;
    height: 32px;
    margin-right: 8px;
  `}
`;

const AvatarWrapper = styled(Link)`
  display: block;
`;

const UsernameCol = styled.div`
  margin-right: 8px;
  flex: 1;
  max-width: calc(100% - 100px);
  font-size: 14px;
  overflow: hidden;
`;

const UsernameRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  ${media.mobile`
    margin-bottom: 4px;
  `}
`;

const UsernameLink = styled(Link)`
  max-width: 100%;
  flex: 0 1 auto;
  display: flex;
  align-items: center;
  cursor: pointer;
  color: ${color.white};
`;

const Username = styled.h1`
  ${TextEllipsis};
  ${ResetHeadingStyle};
  display: inline-block;
  max-width: 100%;
  margin-right: 4px;
  font-size: 16px;
  font-weight: 600;
  ${media.mobile`
    font-size: 14px;
  `}
`;

const PostedTimeRow = styled.div`
  color: ${color.white};
  font-size: 16px;
  opacity: 0.5;
  ${media.mobile`
    font-size: 12px;
  `}
`;

const ScheduledTimeRow = styled(PostedTimeRow)``;

const ActionCol = styled.div`
  margin-left: auto;
  display: flex;
  align-items: center;
`;

const ContextMenuButton = styled.button`
  outline: none;
  margin-right: 4px;
  margin-left: 20px;
  border: 0;
  padding: 0;
  width: 24px;
  height: 24px;
  background: none;
  cursor: pointer;

  ${media.mobile`
    margin-right: 16px;
    margin-left: 16px;
  `}
`;

const CloseButton = styled.button`
  margin: 0;
  border: 0;
  padding: 0;
  background: none;
  width: 24px;
  height: 24px;
  outline: 0;

  & > img {
    width: 100%;
    height: 100%;
  }

  @media (min-width: ${breakpoint.mobile}px) {
    display: none;
  }
`;

const InfoRow = styled.div`
  padding-right: 56px;
  flex: 1;
  overflow-y: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const CaptionCol = styled.div`
  color: ${color.white};
  font-size: 16px;
  font-weight: 600;
  line-height: 1.4;
  display: flex;
  align-items: flex-end;
  word-break: break-word;
  pointer-events: auto;
  padding-bottom: ${({ isTruncated }) => (isTruncated ? '16px' : '12px')};
  ${({ isTruncated }) => (isTruncated ? truncatedSpan : null)};
  ${media.mobile`
    font-size: 14px;
    line-height: 20px;
  `}
`;

const CaptionColWrapper = styled.span`
  ${({ isTruncated }) => (isTruncated ? '' : 'display: inline-block')};
`;

const Caption = styled.div`
  white-space: pre-wrap;
`;

const TagWrapper = styled.div`
  pointer-events: auto;
`;

const BottomTagWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  white-space: nowrap;
  margin-top: 8px;
`;

const truncatedSpan = css`
  & > span {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
    max-width: calc(100% - 21px);
  }
`;

const CaptionPlaceholder = styled.span`
  color: ${color.neutral[75]};
  font-weight: 200;
`;

const ExpandButton = styled.button`
  margin: 0 0 0 5px;
  border: 0;
  padding: 0;
  width: 16px;
  height: 16px;
  flex: none;
  background: none;
  outline: 0;

  & > img {
    width: 100%;
    height: 100%;
    vertical-align: top;

    transform: ${({ isTruncated }) =>
      isTruncated ? 'rotate(0)' : 'rotate(180deg)'};
  }
`;

const MetaPartition = styled.div`
  width: 12px;
  height: 12px;
  margin: 0 4px;
  font-size: 12px;
  color: ${color.neutral[75]};
`;

const TranslationToggle = styled.div`
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: ${color.white};
  ${media.mobile`
    font-size: 12px;
  `}
`;

const WorkFiction = styled.span`
  display: inline-block;
  padding: 4px;
  background-color: ${color.neutral['0_alpha_20']};
  font-size: 12px;
  line-height: 1;
  opacity: 0.5;
  margin-right: 8px;
`;

const CopyRight = styled.span`
  display: inline-block;
  padding: 4px;
  background-color: ${color.neutral['0_alpha_20']};
  font-size: 12px;
  line-height: 1;
  opacity: 0.5;
`;

const UserStatusIndicatorWrapper = styled.div`
  ${({ isLiveStreaming, isOnline }) => {
    if (isLiveStreaming) {
      return css`
        position: absolute;
        left: 50%;
        bottom: 0px;
        transform: translate(-50%, 50%);
      `;
    } else if (isOnline) {
      return css`
        position: absolute;
        bottom: 0px;
        right: ${({ size }) => (size >= 68 ? 4 : 0)}px;
      `;
    }
  }}
`;

const PinnedCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const PinIcon = styled.img`
  margin-right: 4px;
  width: 18px;
  height: 18px;
  object-fit: contain;
  ${media.mobile`
    width: 16px;
    height: 16px;
  `};
`;

const PinText = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${color.neutral[55]};
  ${media.mobile`
    font-size: 12px;
  `};
`;

export default withTranslation()(withFetchTranslation(MessagePanelTop));
